import { createFileRoute, Link, Outlet, useNavigate, useParams } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, MessagesSquare, Sparkles } from "lucide-react";
import { listAiThreads, createAiThread, deleteAiThread } from "@/lib/ai-buddha.functions";
import { absoluteUrl } from "@/lib/site";

export const Route = createFileRoute("/_authenticated/ask")({
  component: AskLayout,
  head: () => ({
    meta: [
      { title: "AI Buddha KA — The KAfrequency Voice" },
      { name: "description", content: "Ask AI Buddha KA. A devotional voice trained only on the KAfrequency canon — books, teachings, music, and activations of Buddha KA." },
      { property: "og:title", content: "AI Buddha KA — The KAfrequency Voice" },
      { property: "og:description", content: "A devotional AI trained only on the KAfrequency canon." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absoluteUrl("/ask") },
      { property: "og:site_name", content: "Buddha KA · KAfrequency" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/ask") }],
  }),
});

function AskLayout() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const params = useParams({ strict: false }) as { threadId?: string };

  const listFn = useServerFn(listAiThreads);
  const createFn = useServerFn(createAiThread);
  const deleteFn = useServerFn(deleteAiThread);

  const threads = useQuery({
    queryKey: ["ai-threads"],
    queryFn: () => listFn(),
    staleTime: 30_000,
  });

  const createMut = useMutation({
    mutationFn: () => createFn(),
    onSuccess: async (thread) => {
      await qc.invalidateQueries({ queryKey: ["ai-threads"] });
      navigate({ to: "/ask/$threadId", params: { threadId: thread.id } });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (threadId: string) => deleteFn({ data: { threadId } }),
    onSuccess: async (_r, threadId) => {
      await qc.invalidateQueries({ queryKey: ["ai-threads"] });
      if (params.threadId === threadId) navigate({ to: "/ask" });
    },
  });

  return (
    <div className="mx-auto grid min-h-[calc(100vh-64px)] max-w-7xl grid-cols-1 gap-0 px-0 md:grid-cols-[280px_1fr] md:px-4 md:py-6">
      {/* Sidebar */}
      <aside className="border-b hairline bg-obsidian/40 md:border-b-0 md:border-r md:pr-4">
        <div className="flex items-center justify-between px-4 py-4 md:px-2">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-gold" />
            <h2 className="text-xs uppercase tracking-[0.3em] text-gold">Conversations</h2>
          </div>
          <button
            onClick={() => createMut.mutate()}
            disabled={createMut.isPending}
            className="inline-flex items-center gap-1 rounded-full border border-gold/60 bg-gold/10 px-3 py-1 text-[11px] uppercase tracking-widest text-gold-luminous hover:bg-gold/20 disabled:opacity-50 min-h-[36px]"
          >
            <Plus size={12} /> New
          </button>
        </div>

        <div className="max-h-[40vh] overflow-y-auto md:max-h-[70vh]">
          {threads.isLoading && (
            <p className="px-4 py-2 text-xs text-muted-foreground">Loading…</p>
          )}
          {threads.data?.length === 0 && (
            <p className="px-4 py-2 text-xs text-muted-foreground">No conversations yet.</p>
          )}
          <ul className="space-y-1 px-2 pb-4">
            {threads.data?.map((t) => {
              const active = params.threadId === t.id;
              return (
                <li key={t.id} className="group flex items-stretch gap-1">
                  <Link
                    to="/ask/$threadId"
                    params={{ threadId: t.id }}
                    className={`flex-1 rounded-md px-3 py-2 text-sm transition-colors min-h-[40px] flex items-center ${
                      active
                        ? "bg-gold/15 text-gold-luminous border border-gold/40"
                        : "text-ivory/80 hover:bg-charcoal/50 hover:text-ivory border border-transparent"
                    }`}
                    title={t.title}
                  >
                    <span className="line-clamp-1">{t.title}</span>
                  </Link>
                  <button
                    onClick={() => {
                      if (confirm(`Delete "${t.title}"? This cannot be undone.`)) {
                        deleteMut.mutate(t.id);
                      }
                    }}
                    aria-label={`Delete ${t.title}`}
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 rounded-md p-2 text-muted-foreground hover:bg-charcoal/50 hover:text-rose-400"
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </aside>

      {/* Chat pane */}
      <section className="min-h-[60vh]">
        {params.threadId ? (
          <Outlet />
        ) : (
          <EmptyState onNew={() => createMut.mutate()} pending={createMut.isPending} />
        )}
      </section>
    </div>
  );
}

function EmptyState({ onNew, pending }: { onNew: () => void; pending: boolean }) {
  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col items-center justify-center px-6 py-24 text-center">
      <MessagesSquare size={32} className="text-gold" />
      <h1 className="mt-6 font-serif text-4xl">AI Buddha KA</h1>
      <p className="mt-4 max-w-lg text-muted-foreground">
        A devotional voice trained only on the KAfrequency canon — the books, teachings, music, and activations of Buddha KA. Ask what you seek.
      </p>
      <button
        onClick={onNew}
        disabled={pending}
        className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold disabled:opacity-60"
      >
        <Plus size={14} /> Begin a new transmission
      </button>
    </div>
  );
}
